import type { HSLColor } from '@design-system/styles'
import { colors, darken, HSLColors, lighten, toHSL, typo } from '@design-system/styles'
import { css } from '@emotion/react'

import type { ButtonColor, ButtonSize } from './_Button'

const buttonHeight = {
  button: {
    xs: 30,
    s: 36,
    m: 40,
    l: 48,
  },
  text: { s: 22, m: 25, l: 32 },
  'icon-square': { s: 42, m: 36, l: 42 },
  'icon-ellipse': { s: 32, m: 36, l: 40 },
  'icon-transparent': { s: 16, m: 28, l: 40 },
}

const buttonColor: Record<
  Exclude<ButtonColor, 'white' | 'default'> & 'lightest-gray' & 'light-gray',
  HSLColor
> = {
  dark: HSLColors.gray.$800,
  red: HSLColors.red.$300,
  blue: HSLColors.green.$300,
  'outline-gray': HSLColors.gray.$100,
  'outline-blue': HSLColors.green.$100,
  'outline-red': HSLColors.red.$100,
  'light-solid-gray': HSLColors.gray.$100,
  square: HSLColors.gray.$100,
}

type ButtonStyleProps = {
  size: ButtonSize
  color?: ButtonColor
}

const defaultButtonStyle = ({ size }: Pick<ButtonStyleProps, 'size'>) => css`
  display: flex;
  justify-content: center;
  align-items: center;

  width: fit-content;
  height: ${buttonHeight.button[size]}px;
  padding: ${(() => {
    switch (size) {
      case 'l':
        return '0 16px'
      case 'm':
        return '0 12px'
      case 's':
        return '0 12px'
      case 'xs':
        return '0 6px'
    }
  })()};

  border-radius: var(--Radius-100);

  //text style
  font-weight: 700;
  color: white;
  white-space: pre-wrap;

  ${size === 'xs' ? typo.caption01 : typo.body02};

  &[aria-busy='true'] {
    cursor: wait;
  }

  &:focus-visible {
    outline: var(--Colors-Green-500) solid 2px;
    outline-offset: 2px;
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
  }

  &.disabled-like,
  &.disabled-like:hover {
    border-color: ${colors.gray.$400};
    background-color: ${colors.gray.$200};
    color: ${colors.gray.$500};
  }

  &.disabled-like:hover {
    cursor: default;
  }
`

export const buttonStyle = {
  primary: ({ size, color }: ButtonStyleProps) => {
    const _color = color ? color : 'blue'
    return css`
      ${defaultButtonStyle({ size })};
      background-color: ${toHSL(buttonColor[_color])};

      &:hover {
        background-color: ${darken(buttonColor[_color], 4)};
      }
      &:active {
        background-color: ${darken(buttonColor[_color], 8)};
      }
      &[aria-busy='true'] {
        background-color: ${lighten(buttonColor[_color], 20)};
      }
      &[aria-disabled='true'] {
        color: ${colors.gray.$500};
        background-color: ${colors.gray.$200};
      }
    `
  },
  ghost: ({ size, color }: ButtonStyleProps) => {
    const _color = color ? color : 'blue'
    return css`
      ${defaultButtonStyle({ size })};
      background-color: transparent;
      border: 1px solid ${toHSL(buttonColor[_color])};
      color: ${toHSL(buttonColor[_color])};
      &:hover {
        border-color: ${darken(buttonColor[_color], 4)};
        color: ${darken(buttonColor[_color], 4)};
      }
      &:active {
        border-color: ${darken(buttonColor[_color], 8)};
        color: ${darken(buttonColor[_color], 8)};
      }
      &[aria-disabled='true'] {
        border-color: ${colors.gray.$400};
        color: ${colors.gray.$400};
      }
    `
  },
  ellipsis: ({ size }: ButtonStyleProps) => {
    return css`
      ${defaultButtonStyle({ size })};
      color: ${colors.gray.$700};
      background: #fff;
      border: 1px dashed ${colors.gray.$500};
      border-radius: var(--Radius-100);

      &:hover {
        background: ${colors.gray.$200};
      }
      &:active {
        background: ${colors.gray.$300};
      }
      &[aria-disabled='true'] {
        color: ${colors.gray.$400};
        background: #fff;
      }
    `
  },
  outline: ({ size, color }: ButtonStyleProps) => {
    const _color = color ? `outline-${color}` : 'outline-gray'

    const fontColor = (() => {
      switch (_color) {
        case 'outline-gray':
          return colors.gray.$900
        case 'outline-blue':
          return 'var(--Colors-Green-500)'
        case 'outline-red':
          return colors.red.$300
      }
    })()
    const borderColor = (() => {
      switch (_color) {
        case 'outline-gray':
          return colors.gray.$400
        case 'outline-blue':
          return 'var(--Colors-Green-200)'
        case 'outline-red':
          return colors.red.$200
      }
    })()
    return css`
      ${defaultButtonStyle({ size })};
      background-color: ${toHSL(buttonColor[_color])};
      border: 1px solid ${borderColor};
      color: ${fontColor};
      font-weight: normal;

      &:hover {
        background-color: ${darken(buttonColor[_color], 4)};
      }
      &:active {
        background-color: ${darken(buttonColor[_color], 8)};
      }
      &[aria-disabled='true'] {
        border-color: ${colors.gray.$400};
        background-color: ${colors.gray.$200};
        color: ${colors.gray.$500};
      }
    `
  },
  'light-solid': ({ size }: ButtonStyleProps) => {
    const color = 'light-solid-gray'

    return css`
      ${defaultButtonStyle({ size })};
      background-color: ${toHSL(buttonColor[color])};
      color: ${colors.gray.$800};
      font-weight: normal;

      &:hover {
        background-color: ${darken(buttonColor[color], 4)};
      }
      &:active {
        background-color: ${darken(buttonColor[color], 8)};
      }
      &[aria-disabled='true'] {
        background-color: ${colors.gray.$200};
        color: ${colors.gray.$500};
      }
    `
  },
  // 추후 확인 필요
  square: ({ size }: ButtonStyleProps) => {
    return css`
      ${defaultButtonStyle({ size })};
      color: ${colors.gray.$600};
      border: 1px solid ${colors.gray.$300};
      border-radius: var(--Radius-100);
      background: transparent;

      &:hover {
        background-color: ${darken(buttonColor['square'], 4)};
      }
      &:not([aria-disabled='true']):active {
        color: var(--Colors-Green-200);
        border: 1px solid var(--Colors-Green-200);
        background-color: ${darken(buttonColor['outline-blue'], 4)};

        > svg {
          color: var(--Colors-Green-500) !important;
        }
      }
      &[aria-disabled='true'] {
        background-color: ${colors.gray.$200};
        color: ${colors.gray.$500};
      }
    `
  },
  text: ({ size }: ButtonStyleProps) => {
    return css`
      ${defaultButtonStyle({ size })};
      color: ${colors.gray.$800};
      background: transparent;
    `
  },
}

const defaultTextButtonStyle = (size: Omit<ButtonSize, 'xs'>) => css`
  width: fit-content;
  display: inline-flex;
  align-items: center;

  &:focus-visible .inner-button {
    outline: var(--Colors-Green-500) solid 2px;
    outline-offset: -2px;
  }

  .inner-button {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    background-color: transparent;
    border-radius: var(--Radius-100);

    width: fit-content;
    white-space: pre-wrap;
    ${typo.body02};

    ${(() => {
      switch (size) {
        case 'l': {
          return typo.body01
        }
        case 'm': {
          return typo.body02
        }
        case 's': {
          return typo.caption01
        }
      }
    })()}

    &[aria-busy='true'] {
      cursor: wait;
    }

    &[aria-disabled='true'] {
      cursor: not-allowed;
    }
  }
`

export const textButtonStyle = {
  default: (size: Omit<ButtonSize, 'xs'>) => {
    return css`
      ${defaultTextButtonStyle(size)};

      .inner-button {
        color: ${toHSL(buttonColor['blue'])};
        font-weight: 700; // 디자인시스템: 700
      }

      &[aria-disabled='true'] .inner-button {
        color: ${colors.gray.$500};
      }
    `
  },
  inverse: (size: Omit<ButtonSize, 'xs'>) => {
    return css`
      ${defaultTextButtonStyle(size)};
      .inner-button {
        color: white;
        font-weight: 700; // 디자인시스템: 700
      }
      &:focus-visible .inner-button {
        outline: white solid 2px;
      }

      &:hover .inner-button {
        background-color: rgba(0, 0, 0, 10%);
      }
      &:active .inner-button {
        background-color: rgba(0, 0, 0, 20%);
      }
      &[aria-disabled='true'] .inner-button {
        opacity: 50%;
      }
    `
  },
}

export const textIconTextButtonStyle = ({
  hslColor,
  size,
}: {
  hslColor: HSLColor
  size: Omit<ButtonSize, 'xs'>
}) => {
  return css`
    ${defaultTextButtonStyle(size)};
    height: inherit;

    .inner-button {
      color: ${toHSL(hslColor)};
    }
    &:focus-visible .inner-button {
      outline: white solid 2px;
    }
    &:hover .inner-button,
    &:active .inner-button {
      color: ${darken(hslColor, 8)};
    }
  `
}

export const dotStyle = () => css`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 8px;
  height: 8px;
  border-radius: var(--Radius-Circle);
  border: 1px solid #fff;
  background: ${colors.red.$400};
`
